import classNames from "classnames";
import React, { FC, useMemo, useRef, useState } from "react";
import { LuFolderPlus, LuUploadCloud, LuX } from "react-icons/lu";

import {
  useCreateFolderMutation,
  useLinkFilesToWorkMutation,
  useRenameFileMutation,
  useUpdateFolderMutation,
  useUploadFilesMutation,
} from "../../slices/DocumentsSlice";
import { FileItem, FolderItem, FolderVisibility, WorkRef } from "./files.types";
import {
  ACCEPT,
  folderChain,
  formatSize,
  uploadProblem,
  visibilityLabel,
} from "./files.utils";
import { FolderSelect } from "./FolderPicker";
import {
  DANGER_BUTTON,
  ErrorText,
  errorText,
  FilesModal,
  FileTypeIcon,
  GHOST_BUTTON,
  INPUT,
  LABEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Toggle,
} from "./ui/FilesUi";

const TOP_CHOICES: Exclude<FolderVisibility, "inherit">[] = [
  "private",
  "shared",
  "uploads",
];

/** What the folder's own setting means for the client, in words. */
const visibilityHint = (visibility: FolderVisibility) => {
  switch (visibility) {
    case "shared":
      return "The client sees the files in this folder in their portal.";
    case "uploads":
      return "The client sees this folder and can upload files into it.";
    case "inherit":
      return "Follows the folder it is in.";
    default:
      return "Only your team sees it.";
  }
};

/**
 * New folder / Edit folder (name and who can see it). With `chooseParent` a
 * new folder also gets an "Inside" dropdown (used where no folder is open).
 */
export const FolderFormModal: FC<{
  customerId: string;
  folders: FolderItem[];
  folder?: FolderItem | null;
  parentId?: string | null;
  chooseParent?: boolean;
  onClose: () => void;
  onSaved?: (folderId: string) => void;
}> = ({
  customerId,
  folders,
  folder,
  parentId: startParent = null,
  chooseParent = false,
  onClose,
  onSaved,
}) => {
  const [parentId, setParentId] = useState<string | null>(startParent);
  const isTop = folder ? !folder.parentId : !parentId;
  const [name, setName] = useState(folder?.name || "");
  const [visibility, setVisibility] = useState<FolderVisibility>(
    folder?.visibility || (isTop ? "private" : "inherit"),
  );
  const [error, setError] = useState("");
  const [createFolder, { isLoading: creating }] = useCreateFolderMutation();
  const [updateFolder, { isLoading: updating }] = useUpdateFolderMutation();
  const busy = creating || updating;
  const parentPath = folderChain(folders, folder ? folder.parentId : parentId)
    .map((f) => f.name)
    .join(" › ");
  const choices: FolderVisibility[] = isTop
    ? TOP_CHOICES
    : ["inherit", ...TOP_CHOICES];

  const showParent = chooseParent && !folder;
  const pickParent = (id: string | null) => {
    setParentId(id);
    // "Same as the folder it's in" only exists inside a folder.
    setVisibility(id ? "inherit" : "private");
  };

  const save = async () => {
    setError("");
    if (!name.trim()) {
      setError("Enter a folder name.");
      return;
    }
    try {
      if (folder) {
        await updateFolder({
          id: folder.id,
          name: name.trim(),
          visibility,
        }).unwrap();
        onSaved?.(folder.id);
      } else {
        const created = await createFolder({
          customerId,
          parentId,
          name: name.trim(),
          visibility,
        }).unwrap();
        onSaved?.(created.id);
      }
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <FilesModal
      title={folder ? "Edit folder" : "New folder"}
      subtitle={
        showParent
          ? undefined
          : parentPath
          ? `Inside ${parentPath}`
          : "At the top level"
      }
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={onClose}
            disabled={busy}>
            Cancel
          </button>
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            onClick={save}
            disabled={busy}>
            {busy ? "Saving…" : folder ? "Save" : "Create folder"}
          </button>
        </>
      }>
      <label htmlFor={"folder-name"} className={LABEL}>
        Folder name
      </label>
      <input
        id={"folder-name"}
        autoFocus={true}
        className={INPUT}
        value={name}
        maxLength={100}
        placeholder={"e.g. Tax return"}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => event.key === "Enter" && save()}
      />
      {showParent && (
        <div className={"mt-4"}>
          <label htmlFor={"folder-parent"} className={LABEL}>
            Inside
          </label>
          <FolderSelect
            id={"folder-parent"}
            folders={folders}
            value={parentId}
            onChange={pickParent}
            topLabel={"Top level"}
          />
        </div>
      )}
      <div className={classNames(LABEL, "mt-4")}>Who can see it</div>
      <div className={"flex flex-col gap-2"}>
        {choices.map((choice) => (
          <label
            key={choice}
            className={classNames(
              "flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5",
              visibility === choice
                ? "border-[#6B4EFF] bg-[#F7F5FF]"
                : "border-[#DCE2EA]",
            )}>
            <input
              type={"radio"}
              name={"folder-visibility"}
              className={"radio radio-primary radio-sm mt-0.5"}
              checked={visibility === choice}
              onChange={() => setVisibility(choice)}
            />
            <span>
              <span className={"block font-bold"}>
                {visibilityLabel(choice)}
              </span>
              <span className={"block text-xs text-[#667085]"}>
                {visibilityHint(choice)}
              </span>
            </span>
          </label>
        ))}
      </div>
      <ErrorText>{error}</ErrorText>
    </FilesModal>
  );
};

/** Rename a file (the extension is kept). */
export const RenameFileModal: FC<{ file: FileItem; onClose: () => void }> = ({
  file,
  onClose,
}) => {
  const dot = file.name.lastIndexOf(".");
  const ext = dot > 0 ? file.name.slice(dot) : "";
  const [name, setName] = useState(
    dot > 0 ? file.name.slice(0, dot) : file.name,
  );
  const [error, setError] = useState("");
  const [renameFile, { isLoading }] = useRenameFileMutation();

  const save = async () => {
    setError("");
    try {
      await renameFile({
        kind: file.kind,
        id: file.id,
        name: `${name.trim()}${ext}`,
      }).unwrap();
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <FilesModal
      title={"Rename file"}
      onClose={onClose}
      busy={isLoading}
      footer={
        <>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={onClose}
            disabled={isLoading}>
            Cancel
          </button>
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            onClick={save}
            disabled={isLoading || !name.trim()}>
            Save
          </button>
        </>
      }>
      <label htmlFor={"file-name"} className={LABEL}>
        File name
      </label>
      <div className={"flex items-center gap-2"}>
        <input
          id={"file-name"}
          autoFocus={true}
          className={INPUT}
          value={name}
          maxLength={140}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && save()}
        />
        {ext && <span className={"font-semibold text-[#667085]"}>{ext}</span>}
      </div>
      <ErrorText>{error}</ErrorText>
    </FilesModal>
  );
};

/** Link files to one of the client's work items (or remove the link). */
export const LinkWorkModal: FC<{
  files: FileItem[];
  works: WorkRef[];
  onClose: () => void;
}> = ({ files, works, onClose }) => {
  const [workId, setWorkId] = useState(
    files.length === 1 ? files[0].workId || "" : "",
  );
  const [error, setError] = useState("");
  const [link, { isLoading }] = useLinkFilesToWorkMutation();

  const save = async () => {
    setError("");
    try {
      await link({
        items: files.map((f) => ({ kind: f.kind, id: f.id })),
        workId: workId || null,
      }).unwrap();
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <FilesModal
      title={"Link to work item"}
      subtitle={files.length === 1 ? files[0].name : `${files.length} files`}
      onClose={onClose}
      busy={isLoading}
      footer={
        <>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={onClose}
            disabled={isLoading}>
            Cancel
          </button>
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            onClick={save}
            disabled={isLoading}>
            Save
          </button>
        </>
      }>
      <label htmlFor={"link-work"} className={LABEL}>
        Work item
      </label>
      <select
        id={"link-work"}
        className={INPUT}
        value={workId}
        onChange={(event) => setWorkId(event.target.value)}>
        <option value={""}>No work item</option>
        {works.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </select>
      <p className={"mt-2 text-xs text-[#667085]"}>
        Linked files also show on the work item’s Files tab.
      </p>
      <ErrorText>{error}</ErrorText>
    </FilesModal>
  );
};

/** "Delete N files?" (they go to the recycle bin). */
export const DeleteFilesModal: FC<{
  title: string;
  description: string;
  busy?: boolean;
  error?: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
}> = ({
  title,
  description,
  busy,
  error,
  confirmLabel = "Delete",
  onClose,
  onConfirm,
}) => (
  <FilesModal
    title={title}
    onClose={onClose}
    busy={busy}
    width={420}
    footer={
      <>
        <button
          type={"button"}
          className={SECONDARY_BUTTON}
          onClick={onClose}
          disabled={busy}>
          Cancel
        </button>
        <button
          type={"button"}
          className={DANGER_BUTTON}
          onClick={onConfirm}
          disabled={busy}>
          {busy ? "Deleting…" : confirmLabel}
        </button>
      </>
    }>
    <p className={"text-[13.5px] leading-relaxed text-[#4A5168]"}>
      {description}
    </p>
    <ErrorText>{error}</ErrorText>
  </FilesModal>
);

/**
 * The upload window used everywhere: client Files tab, work Files tab and
 * the profile card. Pick a folder, an optional work item, share and email.
 */
export const UploadFilesModal: FC<{
  customerId?: string | null;
  clientName?: string;
  folders: FolderItem[];
  works: WorkRef[];
  folderId?: string | null;
  workId?: string | null;
  lockWork?: boolean;
  initialFiles?: File[];
  portalActive?: boolean;
  onClose: () => void;
  onDone?: (count: number) => void;
}> = ({
  customerId,
  clientName,
  folders,
  works,
  folderId: startFolder = null,
  workId: startWork = null,
  lockWork,
  initialFiles = [],
  portalActive = true,
  onClose,
  onDone,
}) => {
  const [files, setFiles] = useState<File[]>(initialFiles);
  const [folderId, setFolderId] = useState<string | null>(startFolder);
  const [workId, setWorkId] = useState<string | null>(startWork);
  const [share, setShare] = useState(false);
  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [upload, { isLoading }] = useUploadFilesMutation();

  const folder = folders.find((f) => f.id === folderId);
  const [newFolder, setNewFolder] = useState<string | null>(null);
  const [createFolder, { isLoading: creatingFolder }] =
    useCreateFolderMutation();

  /** Makes a folder inside the chosen one (or at the top) and picks it. */
  const addFolder = async () => {
    if (!customerId || !newFolder?.trim()) return;
    setError("");
    try {
      const created = await createFolder({
        customerId,
        parentId: folderId,
        name: newFolder.trim(),
      }).unwrap();
      setNewFolder(null);
      setFolderId(created.id);
    } catch (e) {
      setError(errorText(e));
    }
  };
  const folderVisible = !!folder && folder.effective !== "private";
  const visible = folderVisible || share;
  const problem = useMemo(
    () => (files.length ? uploadProblem(files) : null),
    [files],
  );

  const add = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    // Copy the files NOW: the browser's FileList is live and is emptied when
    // the input is cleared right after this call. Reading it later (inside
    // the state updater, which React may run on the next render, e.g. after
    // a Folder was picked) gave an empty list and the files never appeared.
    const chosen = Array.from(list);
    setError("");
    setFiles((current) => [...current, ...chosen]);
  };

  const submit = async () => {
    setError("");
    const check = uploadProblem(files);
    if (check) {
      setError(check);
      return;
    }
    try {
      const added = await upload({
        customerId: customerId || null,
        folderId,
        workId,
        share: share && !folderVisible,
        notify: visible && notify,
        message: message.trim() || undefined,
        files,
      }).unwrap();
      onDone?.(added.length);
      onClose();
    } catch (e) {
      setError(errorText(e, "The upload didn't work. Please try again."));
    }
  };

  return (
    <FilesModal
      title={
        files.length > 0
          ? `Upload ${files.length} file${files.length === 1 ? "" : "s"}`
          : "Upload files"
      }
      subtitle={clientName}
      onClose={onClose}
      busy={isLoading}
      width={520}
      footer={
        <>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={onClose}
            disabled={isLoading}>
            Cancel
          </button>
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            onClick={submit}
            disabled={isLoading || files.length === 0 || !!problem}>
            {isLoading ? "Uploading…" : "Upload"}
          </button>
        </>
      }>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          add(event.dataTransfer.files);
        }}
        className={classNames(
          "rounded-xl border-[1.5px] border-dashed px-4 py-4 text-center",
          dragging ? "border-[#6B4EFF] bg-[#F7F5FF]" : "border-[#DCE2EA]",
        )}>
        <LuUploadCloud size={22} className={"mx-auto text-[#6B4EFF]"} />
        <div className={"mt-1 text-[13px] text-[#4A5168]"}>
          Drag files here or{" "}
          <button
            type={"button"}
            className={"font-bold text-[#5A3EEB] underline"}
            onClick={() => inputRef.current?.click()}>
            choose files
          </button>
        </div>
        <div className={"text-xs text-[#9AA0B0]"}>
          Up to 50 MB each · PDF, photos, Word, Excel, CSV, ZIP
        </div>
        <input
          ref={inputRef}
          id={"upload-files-input"}
          type={"file"}
          multiple={true}
          accept={ACCEPT}
          className={"hidden"}
          onChange={(event) => {
            add(event.target.files);
            event.target.value = "";
          }}
        />
      </div>
      {files.length > 0 && (
        <ul
          className={
            "mt-3 flex max-h-[160px] flex-col gap-1.5 overflow-y-auto"
          }>
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className={
                "flex items-center gap-2.5 rounded-lg bg-[#F7F8FA] px-2.5 py-1.5"
              }>
              <FileTypeIcon name={file.name} size={24} />
              <span className={"min-w-0 flex-1 truncate font-semibold"}>
                {file.name}
              </span>
              <span className={"text-xs text-[#667085]"}>
                {formatSize(file.size)}
              </span>
              <button
                type={"button"}
                aria-label={`Remove ${file.name}`}
                className={"text-[#667085] hover:text-[#C2353A]"}
                onClick={() =>
                  setFiles((current) => current.filter((_, i) => i !== index))
                }>
                <LuX size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {customerId && (
        <div className={"mt-4"}>
          <label htmlFor={"upload-folder"} className={LABEL}>
            Folder
          </label>
          <FolderSelect
            id={"upload-folder"}
            folders={folders}
            value={folderId}
            onChange={setFolderId}
          />
          <div className={"mt-2"}>
            {newFolder === null ? (
              <button
                type={"button"}
                className={GHOST_BUTTON}
                onClick={() => setNewFolder("")}>
                <LuFolderPlus size={15} />
                New folder {folder ? `in “${folder.name}”` : "at the top level"}
              </button>
            ) : (
              <div className={"flex gap-2"}>
                <input
                  id={"upload-new-folder"}
                  autoFocus={true}
                  className={INPUT}
                  placeholder={"Folder name"}
                  value={newFolder}
                  maxLength={100}
                  onChange={(event) => setNewFolder(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") addFolder();
                    if (event.key === "Escape") {
                      event.stopPropagation();
                      setNewFolder(null);
                    }
                  }}
                />
                <button
                  type={"button"}
                  className={PRIMARY_BUTTON}
                  disabled={creatingFolder || !newFolder.trim()}
                  onClick={addFolder}>
                  Add
                </button>
                <button
                  type={"button"}
                  className={SECONDARY_BUTTON}
                  onClick={() => setNewFolder(null)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {works.length > 0 && (
        <div className={"mt-3"}>
          <label htmlFor={"upload-work"} className={LABEL}>
            Link to work item (optional)
          </label>
          <select
            id={"upload-work"}
            className={INPUT}
            disabled={lockWork}
            value={workId || ""}
            onChange={(event) => setWorkId(event.target.value || null)}>
            <option value={""}>No work item</option>
            {works.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {customerId && (
        <div className={"mt-4 flex flex-col gap-3"}>
          <Toggle
            id={"upload-share"}
            checked={visible}
            disabled={folderVisible}
            label={"Share with client"}
            hint={
              folderVisible
                ? "On: this folder is shared with the client"
                : "The client sees these files in their portal"
            }
            onChange={setShare}
          />
          {visible && (
            <Toggle
              id={"upload-notify"}
              checked={notify}
              disabled={!portalActive}
              label={"Email the client that files are ready"}
              hint={
                portalActive
                  ? "Sent to the client’s portal users"
                  : "Invite the client to the portal first"
              }
              onChange={setNotify}
            />
          )}
          {visible && notify && (
            <textarea
              id={"upload-message"}
              className={classNames(INPUT, "h-20 py-2")}
              placeholder={"Add a message (optional)"}
              maxLength={1000}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          )}
        </div>
      )}
      <ErrorText>{error || problem}</ErrorText>
    </FilesModal>
  );
};
